"use client";

import { Toaster } from "@/components/ui/toaster";
import { toast } from "@/components/ui/use-toast";
import useAuthStore from "@/stores/useAuthStore";
import { useCartStore2 } from "@/stores/cartStore";
import axiosInstance from "@/utils/axiosInstance";
import { getFbc } from "@/utils/getFbcMeta";
import { getFbp } from "@/utils/getFbpMeta";
import { getContentIds } from "@/utils/metaContentIds";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { v4 as uuidv4 } from "uuid";

interface ProductCardProps {
  product: any;
}

export default function ProductCard({ product }: ProductCardProps) {
  const { addItem } = useCartStore2();
  const router = useRouter();
  const { userMetadata, userData } = useAuthStore();

  const firstVariant =
    product.type === "variant" &&
    product.variants &&
    product.variants.length > 0
      ? product.variants[0]
      : null;

  const displayPrice = firstVariant
    ? firstVariant.discounted_price || firstVariant.price
    : product.discounted_price || product.price;

  const originalPrice = firstVariant ? firstVariant.price : product.price;

  const handleAddProduct = () => {
    const eventId = uuidv4(); // ✅ Se genera una sola vez por evento

    const productToAdd = {
      product_id: product.id,
      name: product.name,
      price: firstVariant ? firstVariant.price : product.price,
      discounted_price: firstVariant
        ? firstVariant.discounted_price
        : product.discounted_price,
      usd_price: firstVariant ? firstVariant.usd_price : product.usd_price,
      discounted_usd_price: firstVariant
        ? firstVariant.discounted_usd_price
        : product.discounted_usd_price,
      thumbnail_url: firstVariant
        ? firstVariant.thumbnail_url
        : product.thumbnail_url,
      slug: product.slug,
      type: product.type,
      sku: firstVariant ? firstVariant.sku : product.sku || "",
      grams: firstVariant ? firstVariant.grams : product.grams || 0,
      variant_id: firstVariant ? firstVariant.id : undefined,
    };

    const total =
      productToAdd.discounted_price != null
        ? productToAdd.discounted_price
        : productToAdd.price;


  // 🔵 Construir content_ids en base al tipo
  const contentIds = getContentIds([
    {
          productId: productToAdd.product_id,
          qty: 1,
        },
  ]);

    const value = Number(total);
    const currency = "CLP";

    // ✅ Pixel
    if (typeof window.fbq === "function") {
      window.fbq("track", "AddToCart", {
        content_name: productToAdd.name,
        content_ids: contentIds,
        content_type: "product",
        value,
        currency,
        event_id: eventId,
      });
    }

    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "add_to_cart",
        event_id: eventId, 
        ecommerce: {
          currency: currency,
          value: value,
          items: [
            {
              item_id: String(productToAdd.product_id),
              item_name: productToAdd.name,
              price: Number(productToAdd.discounted_price ?? productToAdd.price),
              quantity: 1,
              sku: productToAdd.sku || undefined,
            },
          ],
        },
      }); 
    }

    // ✅ CAPI
    axiosInstance.post(`${process.env.NEXT_PUBLIC_API_URL}/meta/add-to-cart`, {
      event_id: eventId,
      userData: {
        ...(userData?.email && { email: userData.email }),
        ...(userMetadata?.first_name && { first_name: userMetadata.first_name }),
        ...(userMetadata?.last_name && { last_name: userMetadata.last_name }),
        ...(getFbc() && { fbc: getFbc() }),
        ...(getFbp() && { fbp: getFbp() }),
      },
      product: {
        content_ids: contentIds,
        content_name: productToAdd.name,
        value,
        currency,
        content_type: "product",
      },
      action_source: "website"
    });

    addItem({
      line_type: productToAdd.type,
      product_id: productToAdd.product_id,
      variant_id: productToAdd.variant_id,
      quantity: 1,
    });

    toast({
      title: "Producto añadido al carrito",
      image: productToAdd.thumbnail_url,
      description: `1 x ${product.name} añadido al carrito.`,
      duration: 3000,
    });

    //console.log("🛒 AddToCart enviado con event_id:", eventId);
  };

  const formatPrice = (price: any) => {
    if (price === null || price === undefined) {
      return ""; // Or handle null/undefined as needed
    }
    return price.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
    });
  };

  const hasAvailableStock = () => {
    if (product.type === "variant" && product.variants) {
      return product.variants.some((variant: any) => variant.stock > 0);
    }
    if (product.type === "bundle" && product.bundle_items) {
      // Verificar si todos los productos del bundle tienen stock
      return product.bundle_items.every((item: any) => item.stock > 0);
    }
    return product.stock > 0;
  };

  return (
    <>
      <div className="flex flex-col  rounded-md">
        <Link href={"/products/" + product.slug}>
          <Image
            src={firstVariant?.thumbnail_url || product.thumbnail_url}
            alt={product.name}
            className="w-full max-h-80 object-cover"
            width={300}
            height={192}
          />
        </Link>
        <div className="p-4">
          <div className="text-center">
            <Link href={"/products/" + product.slug}>
              <h3 className="text-lg font-semibold text-black mt-2">
                {product.name}
              </h3>
            </Link>
            <p className="text-gray-500 mt-1">{formatPrice(displayPrice)}</p>
            {displayPrice !== originalPrice && (
              <p className="text-xs text-bright-pink line-through">
                {formatPrice(originalPrice)}
              </p>
            )}
          </div>
          <div>
            {hasAvailableStock() ? (
              product.type === "variant"  ? (
                <button
                  onClick={() => router.push(`/products/${product.slug}`)}
                  className="border border-black text-black w-full mt-2 p-2 rounded-full font-medium"
                >
                  ELEGIR OPCIONES
                </button>
              ) : product.type === "bundle" ? (
                <button
                  onClick={() => router.push(`/products/${product.slug}`)}
                  className="border border-black text-black w-full mt-2 p-2 rounded-full font-medium"
                >
                  ELEGIR OPCIONES
                </button>
              ) : (
                <button
                  onClick={handleAddProduct}
                  className="border border-black text-black w-full mt-2 p-2 rounded-full font-medium"
                >
                  AÑADIR AL CARRITO
                </button>
              )
            ) : (
              <button
                disabled
                className="border border-gray-300 text-gray-300 w-full mt-2 p-2 rounded-full font-medium cursor-not-allowed"
              >
                {product.type === "bundle" ? "PACK SIN STOCK" : "SIN STOCK"}
              </button>
            )}
          </div>
        </div>
        <Toaster />
      </div>
    </>
  );
}
